import { useLayoutEffect, useMemo } from "react";

import { useUser } from "@/auth/UserContext";
import { useUserPreferences } from "@/context/UserPreferencesContext";
import { isFridayInTz } from "@/lib/myBookingsScope";
import {
  EMPTY_VIEWER_SCOPE,
  resolveViewerBookingScope,
  setLiveViewerScope,
  type ViewerBookingScope,
} from "@/lib/viewerScopeFilters";
import { api } from "@/services/api";

/** Current linked-bookings scope: locked for Sales, preference-gated for ops. */
export function useViewerBookingScope(): ViewerBookingScope {
  const { isSalesPerson, isClient, profile } = useUser();
  const { preferences } = useUserPreferences();

  return useMemo(
    () =>
      resolveViewerBookingScope({
        isSalesPerson,
        isClient,
        salesPersonLink: profile?.sales_person_link,
        openedByValues: profile?.opened_by_values,
        preferenceOn: preferences.scopeToMyBookings,
        fridayOverride: isFridayInTz(),
      }),
    [
      isSalesPerson,
      isClient,
      profile?.sales_person_link,
      profile?.opened_by_values,
      preferences.scopeToMyBookings,
    ],
  );
}

/** Keep the API client in sync so every list/metrics call inherits the scope. */
export function useSyncViewerBookingScope() {
  const scope = useViewerBookingScope();
  // Write during render so child fetches in this commit never see a stale
  // empty singleton (useEffect runs too late for the first metrics query).
  api.setViewerScope(scope);
  setLiveViewerScope(scope);
  useLayoutEffect(() => {
    api.setViewerScope(scope);
    setLiveViewerScope(scope);
    return () => {
      api.setViewerScope(EMPTY_VIEWER_SCOPE);
      setLiveViewerScope(EMPTY_VIEWER_SCOPE);
    };
  }, [scope]);
}
